import React, { useState, useEffect } from 'react';
import { notificationApi } from '../services/api';
import { Card, Button, Badge, Spinner } from '../components/UI';
import { Bell, Check, Warning, Clock, CurrencyDollar, ListChecks, CheckCircle, Eye } from '@phosphor-icons/react';

const TYPE_CONFIG = {
  budget_overrun: { icon: CurrencyDollar, color: '#FF3333', variant: 'danger' },
  delayed_task: { icon: Warning, color: '#FFAB00', variant: 'warning' },
  pending_approval: { icon: Clock, color: '#002FA7', variant: 'primary' },
  daily_reminder: { icon: ListChecks, color: '#00C853', variant: 'success' },
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    const res = await notificationApi.list();
    setNotifications(res.data);
  };

  useEffect(() => { load().finally(() => setLoading(false)); }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await notificationApi.generate();
      alert(`Generated ${res.data.count} new notifications`);
      load();
    } catch (err) { alert('Failed to generate'); }
    finally { setGenerating(false); }
  };

  const handleMarkRead = async (id) => {
    await notificationApi.markRead(id);
    load();
  };

  const handleMarkAllRead = async () => {
    await notificationApi.markAllRead();
    load();
  };

  const unread = notifications.filter(n => !n.is_read).length;

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Notifications & Alerts</h1>
          <p className="text-sm text-[#666]">Budget overruns, delayed tasks, pending approvals & reminders</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleGenerate} disabled={generating} data-testid="generate-alerts-btn">
            <Bell className="w-4 h-4 mr-2" /> {generating ? 'Scanning...' : 'Scan for Alerts'}
          </Button>
          {unread > 0 && (
            <Button variant="secondary" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
              <Check className="w-4 h-4 mr-2" /> Mark All Read
            </Button>
          )}
        </div>
      </div>

      {unread > 0 && (
        <div className="bg-[#FFAB00]/10 text-[#FFAB00] px-4 py-2 rounded-lg text-sm font-bold" data-testid="unread-count">
          {unread} unread notification{unread !== 1 ? 's' : ''}
        </div>
      )}

      {notifications.length === 0 ? (
        <Card className="p-8 text-center text-[#666]">
          <Bell className="w-10 h-10 mx-auto mb-3 text-[#E0E0E0]" />
          <p>No notifications. Click "Scan for Alerts" to check for issues.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {notifications.map(n => {
            const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.daily_reminder;
            const Icon = cfg.icon;
            return (
              <Card key={n.id} className={`p-3 sm:p-4 ${!n.is_read ? 'border-l-4 border-l-[#002FA7]' : 'opacity-70'}`} data-testid={`notif-row-${n.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0`} style={{ backgroundColor: `${cfg.color}15` }}>
                    <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold">{n.title}</span>
                      <Badge variant={cfg.variant}>{n.type.replace('_', ' ')}</Badge>
                      {!n.is_read && <span className="w-2 h-2 bg-[#002FA7] rounded-full" />}
                    </div>
                    <p className="text-xs text-[#666] mt-0.5">{n.message}</p>
                    <p className="text-[10px] text-[#999] mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                  {!n.is_read && (
                    <button onClick={() => handleMarkRead(n.id)} className="p-1 text-[#666] hover:text-[#002FA7] shrink-0" data-testid={`mark-read-${n.id}`}>
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
